import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/index.ts";
import { readSchedulesOrganigram } from "../../../services/branchSchedule.service.ts";
import { getProfileImageByCurp } from "../../../services/profileImageCache.ts";
import { NodeDatum } from "../../../interfaces/others/INodeDatum.ts";
import { readBranches } from "../../../services/branch.service.ts";
import ModalContainer from "../../../components/ModalContainer.tsx";
import { getAntiguedad } from "../../../utils/constanst.js";

interface OrgNode extends NodeDatum {
  partnerId?: number;
  candidate?: {
    recruitmentDate?: string;
  };
}

interface Branch {
  id: number;
  name: string;
}

const OrganigramaGraphs: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  const [orgChart, setOrgChart] = React.useState<OrgNode | null>(null);
  const [imagesByCurp, setImagesByCurp] = React.useState<Record<string, string | null>>({});
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [branches, setBranches] = React.useState<Branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = React.useState<number | null>(
    user?.branchId ?? null
  );
  const [selectedNode, setSelectedNode] = React.useState<OrgNode | null>(null);
  const [showModal, setShowModal] = React.useState<boolean>(false);

  const fetchData = React.useCallback(async () => {
    if (!selectedBranchId) {
      setError("Selecciona una sucursal para ver el organigrama.");
      setOrgChart(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await readSchedulesOrganigram(Number(selectedBranchId));
      setOrgChart(data || null);
    } catch (e) {
      console.error("Error fetching organigrama:", e);
      setError("No se pudo cargar el organigrama.");
      setOrgChart(null);
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId]);

  React.useEffect(() => {
    const loadBranches = async () => {
      try {
        const response = await readBranches();
        const list: Branch[] = response || [];
        setBranches(list);

        if (!selectedBranchId) {
          const fromUser = user?.branchId;
          const fallback = list[0]?.id;
          setSelectedBranchId(fromUser ?? fallback ?? null);
        }
      } catch (e) {
        console.error("Error fetching branches:", e);
      }
    };

    loadBranches();
  }, [user?.branchId]);

  React.useEffect(() => {
    if (selectedBranchId) {
      fetchData();
    }
  }, [selectedBranchId, fetchData]);

  const ensureProfileImage = React.useCallback(
    async (curp?: string) => {
      if (!curp || imagesByCurp[curp] !== undefined) return;
      try {
        const url = await getProfileImageByCurp(curp);
        setImagesByCurp((prev) => ({
          ...prev,
          [curp]: url,
        }));
      } catch {
        setImagesByCurp((prev) => ({
          ...prev,
          [curp]: null,
        }));
      }
    },
    [imagesByCurp]
  );

  const getBorderColor = (node: OrgNode) => {
    if (node.assistanceCheck?.status === "Asistencia") {
      return "#16a34a";
    }
    if (!node.startTime) {
      return "#9ca3af";
    }

    const [startHour, startMinute] = node.startTime
      .split(" - ")[0]
      .split(":")
      .map(Number);
    const now = new Date();
    const checkInTime = new Date();
    checkInTime.setHours(startHour, startMinute, 0, 0);

    const diffInMinutes = (now.getTime() - checkInTime.getTime()) / (1000 * 60);

    if (diffInMinutes < 0) {
      return "#9ca3af";
    }
    if (diffInMinutes <= 15) {
      return "#eab308";
    }
    return "#ef4444";
  };

  const renderEmployee = (node: OrgNode) => {
    if (!node) return null;

    const curpToUse = node.curp;
    if (curpToUse) {
      void ensureProfileImage(curpToUse);
    }

    const profileImage =
      (curpToUse && imagesByCurp[curpToUse]) ||
      "https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png";

    const borderColor = getBorderColor(node);

    return (
      <div
        key={node.id}
        style={styles.card}
        onClick={() => {
          setSelectedNode(node);
          setShowModal(true);
        }}
      >
        <div style={styles.avatarContainer}>
          <img
            src={profileImage}
            alt={node.name}
            style={{
              ...styles.avatarImage,
              borderColor,
            }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src =
                "https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png";
            }}
          />
        </div>
        <div style={styles.cardContent}>
          <div style={styles.cardName} title={node.name}>
            {node.name}
          </div>
          <div style={styles.cardPosition} title={node.position}>
            {node.position}
          </div>
          <div style={styles.cardShift}>{node.shift}</div>
        </div>
      </div>
    );
  };

  const getEmployeesByShift = (shift: string) => {
    if (!orgChart) return [];
    const employees: OrgNode[] = [];

    if (orgChart.shift === shift) {
      employees.push(orgChart);
    }

    const traverse = (node: OrgNode) => {
      node.children?.forEach((child) => {
        if (child.shift === shift) {
          employees.push(child as OrgNode);
        }
        traverse(child as OrgNode);
      });
    };

    traverse(orgChart);
    // Ordenar para que el "Líder de Servicio SUC 1" (y líderes de servicio) aparezcan primero
    const getPriority = (node: OrgNode) => {
      const pos = (node.position || "").toLowerCase();
      if (pos.includes("líder de servicio suc 1")) return 2;
      if (pos.includes("líder de servicio suc")) return 1;
      return 0;
    };

    return employees.sort((a, b) => getPriority(b) - getPriority(a));
  };

  const shifts = ["Matutino", "Intermedio", "Vespertino"];

  const attendanceStats = React.useMemo(() => {
    if (!orgChart) {
      return {
        present: 0,
        late: 0,
        absent: 0,
      };
    }

    const allNodes: OrgNode[] = [];
    const collect = (node: OrgNode) => {
      allNodes.push(node);
      node.children?.forEach((child) => collect(child as OrgNode));
    };

    collect(orgChart);

    let present = 0;
    let late = 0;
    let absent = 0;

    allNodes.forEach((node) => {
      const status = (node.assistanceCheck?.status || "").toLowerCase();
      if (!status) return;

      if (status === "asistencia") {
        present += 1;
      } else if (
        status.includes("retardo") ||
        status.includes("tarde") ||
        status.includes("retraso")
      ) {
        late += 1;
      } else if (
        status.includes("falta") ||
        status.includes("no llegó") ||
        status.includes("no llego")
      ) {
        absent += 1;
      }
    });

    return { present, late, absent };
  }, [orgChart]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.headerLeft}>
            <h3 style={styles.title}>Organigrama de sucursal</h3>
          </div>
          <div style={styles.headerRight}>
            <select
              style={styles.branchSelect}
              value={selectedBranchId ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedBranchId(value ? Number(value) : null);
              }}
            >
              <option value="" disabled>
                Selecciona sucursal
              </option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={styles.loadingBody}>
          <i
            className="fa-solid fa-spinner fa-spin"
            style={{ fontSize: 22, color: "#6b7280" }}
          ></i>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.headerLeft}>
            <h3 style={styles.title}>Organigrama de sucursal</h3>
          </div>
          <div style={styles.headerRight}>
            <select
              style={styles.branchSelect}
              value={selectedBranchId ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedBranchId(value ? Number(value) : null);
              }}
            >
              <option value="" disabled>
                Selecciona sucursal
              </option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={styles.errorBody}>{error}</div>
      </div>
    );
  }

  if (!orgChart) {
    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.headerLeft}>
            <h3 style={styles.title}>Organigrama de sucursal</h3>
          </div>
          <div style={styles.headerRight}>
            <select
              style={styles.branchSelect}
              value={selectedBranchId ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedBranchId(value ? Number(value) : null);
              }}
            >
              <option value="" disabled>
                Selecciona sucursal
              </option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={styles.errorBody}>No hay información de organigrama.</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <div style={styles.headerLeft}>
          <h3 style={styles.title}>Organigrama de sucursal</h3>
        </div>
        <div style={styles.headerRight}>
          <select
            style={styles.branchSelect}
            value={selectedBranchId ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedBranchId(value ? Number(value) : null);
            }}
          >
            <option value="" disabled>
              Selecciona sucursal
            </option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          {selectedBranchId && (
            <button
              type="button"
              style={styles.scheduleButton}
              onClick={() =>
                navigate(`/sucursales/horario/${selectedBranchId}`)
              }
            >
              <i
                className="fa-solid fa-calendar-plus"
                style={styles.scheduleButtonIcon}
              ></i>
              <span style={styles.scheduleButtonText}>Programar horario</span>
            </button>
          )}
        </div>
      </div>

      <div style={styles.statsRow}>
        <div style={{ ...styles.statsCard, ...styles.statsCardPresent }}>
          <div style={styles.statsIconWrapper}>
            <i className="fa-solid fa-circle-check" style={styles.statsIcon}></i>
          </div>
          <div style={styles.statsContent}>
            <div style={styles.statsValue}>{attendanceStats.present}</div>
            <div style={styles.statsLabel}>Asistieron</div>
          </div>
        </div>
        <div style={{ ...styles.statsCard, ...styles.statsCardLate }}>
          <div style={styles.statsIconWrapper}>
            <i className="fa-solid fa-clock" style={styles.statsIcon}></i>
          </div>
          <div style={styles.statsContent}>
            <div style={styles.statsValue}>{attendanceStats.late}</div>
            <div style={styles.statsLabel}>Retardos</div>
          </div>
        </div>
        <div style={{ ...styles.statsCard, ...styles.statsCardAbsent }}>
          <div style={styles.statsIconWrapper}>
            <i className="fa-solid fa-triangle-exclamation" style={styles.statsIcon}></i>
          </div>
          <div style={styles.statsContent}>
            <div style={styles.statsValue}>{attendanceStats.absent}</div>
            <div style={styles.statsLabel}>No llegaron</div>
          </div>
        </div>
      </div>

      <div style={styles.centerRow}>
        {renderEmployee(orgChart)}
      </div>

      <div style={styles.shiftsGrid}>
        {shifts.map((shift) => {
          const employees = getEmployeesByShift(shift);
          const leader = employees.find((node) =>
            (node.position || "").toLowerCase().includes("líder de servicio suc 1")
          );
          const others = employees.filter((n) => n !== leader);
          return (
            <div key={shift} style={styles.shiftColumn}>
              <div style={styles.shiftHeader}>{shift}</div>
              {employees.length === 0 ? (
                <div style={styles.emptyShift}>Sin colaboradores en este turno</div>
              ) : (
                <>
                  <div style={styles.shiftLeaderRow}>
                    {leader ? (
                      renderEmployee(leader)
                    ) : (
                      <div style={{ ...styles.card, visibility: "hidden" }} />
                    )}
                  </div>
                  {others.length > 0 && (
                    <div style={styles.shiftEmployeesRow}>
                      {others.map((node) => renderEmployee(node))}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {selectedNode && (
        <ModalContainer
          open={showModal}
          title="Detalles del colaborador"
          closeFn={() => setShowModal(false)}
          size="lg"
        >
          <div style={styles.modalContent}>
            <div style={styles.modalHeaderRow}>
              <div style={styles.modalAvatarWrapper}>
                <img
                  src={
                    (selectedNode.curp && imagesByCurp[selectedNode.curp]) ||
                    "https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png"
                  }
                  alt={selectedNode.name}
                  style={styles.modalAvatar}
                />
              </div>
              <div style={styles.modalMainInfo}>
                <div style={styles.modalName}>{selectedNode.name}</div>
                <div style={styles.modalPosition}>{selectedNode.position}</div>
                <div style={styles.modalShift}>{selectedNode.shift}</div>
                {selectedNode.partnerId && (
                  <a
                    href={`/colaboradores/detalles/${selectedNode.partnerId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.modalProfileLink}
                  >
                    Ver perfil completo
                  </a>
                )}
              </div>
            </div>

            <div style={styles.modalDetailsGrid}>
              <div style={styles.modalDetailCard}>
                <div style={styles.modalDetailLabel}>Antigüedad</div>
                <div style={styles.modalDetailValue}>
                  {getAntiguedad(selectedNode.candidate?.recruitmentDate) ||
                    "No disponible"}
                </div>
              </div>
              <div style={styles.modalDetailCard}>
                <div style={styles.modalDetailLabel}>Horario</div>
                <div style={styles.modalDetailValue}>
                  {selectedNode.startTime && selectedNode.endTime
                    ? `${selectedNode.startTime.split(" - ")[0]} - ${
                        selectedNode.endTime.split(" - ").slice(-1)[0]
                      }`
                    : "No disponible"}
                </div>
              </div>
              <div style={styles.modalDetailCard}>
                <div style={styles.modalDetailLabel}>Estatus asistencia</div>
                <div style={styles.modalDetailValue}>
                  {selectedNode.assistanceCheck?.status || "Sin registro"}
                </div>
              </div>
            </div>
          </div>
        </ModalContainer>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.06)",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  headerLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  title: {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
    color: "#111827",
  },
  subtitle: {
    fontSize: 12,
    color: "#6b7280",
  },
  branchSelect: {
    minWidth: 180,
    padding: "6px 10px",
    borderRadius: 12,
    border: "1px solid #d1d5db",
    backgroundColor: "#f9fafb",
    fontSize: 12,
    color: "#111827",
    fontWeight: 600,
    outline: "none",
  },
  scheduleButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 12,
    border: "1px solid #1d4ed8",
    background:
      "linear-gradient(135deg, rgba(37,99,235,1) 0%, rgba(59,130,246,1) 100%)",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(37, 99, 235, 0.35)",
    whiteSpace: "nowrap",
  },
  scheduleButtonIcon: {
    fontSize: 13,
  },
  scheduleButtonText: {
    fontSize: 12,
  },
  loadingBody: {
    padding: "32px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  errorBody: {
    padding: "24px 0",
    fontSize: 13,
    color: "#b91c1c",
  },
  centerRow: {
    display: "flex",
    justifyContent: "center",
    marginTop: 8,
    marginBottom: 8,
  },
  shiftsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
    marginTop: 8,
  },
  shiftColumn: {
    borderRadius: 0,
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  shiftHeader: {
    fontSize: 13,
    fontWeight: 700,
    color: "#111827",
    textAlign: "center",
    textTransform: "uppercase",
  },
  emptyShift: {
    fontSize: 12,
    color: "#9ca3af",
    padding: "8px 0",
  },
  shiftLeaderRow: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 32,
  },
  shiftEmployeesRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-start",
  },
  card: {
    width: 128,
    padding: 4,
    borderRadius: 0,
    border: "none",
    backgroundColor: "transparent",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
  },
  avatarContainer: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    overflow: "hidden",
    backgroundColor: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
    borderWidth: 3,
    borderStyle: "solid",
  },
  cardContent: {
    textAlign: "center",
    width: "100%",
  },
  cardName: {
    fontSize: 12,
    fontWeight: 600,
    color: "#111827",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cardPosition: {
    fontSize: 11,
    color: "#4b5563",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginTop: 2,
  },
  cardShift: {
    fontSize: 10,
    color: "#9ca3af",
    marginTop: 2,
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 6,
    marginTop: 6,
    marginBottom: 2,
  },
  statsCard: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 8px",
    borderRadius: 8,
    background: "#f9fafb",
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
    border: "1px solid #e5e7eb",
  },
  statsIconWrapper: {
    width: 22,
    height: 22,
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statsIcon: {
    fontSize: 14,
    color: "#ffffff",
  },
  statsContent: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },
  statsLabel: {
    fontSize: 9,
    color: "#e5e7eb",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  statsValue: {
    fontSize: 18,
    fontWeight: 700,
    color: "#ffffff",
  },
  statsCardPresent: {
    background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)",
    borderColor: "rgba(22, 163, 74, 0.5)",
  },
  statsCardLate: {
    background: "linear-gradient(135deg, #facc15 0%, #f97316 100%)",
    borderColor: "rgba(245, 158, 11, 0.6)",
  },
  statsCardAbsent: {
    background: "linear-gradient(135deg, #f97373 0%, #ef4444 100%)",
    borderColor: "rgba(239, 68, 68, 0.6)",
  },
  modalContent: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  modalHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  modalAvatarWrapper: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    overflow: "hidden",
    backgroundColor: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalAvatar: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
  },
  modalMainInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  modalName: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  modalPosition: {
    fontSize: 14,
    color: "#4b5563",
  },
  modalShift: {
    fontSize: 12,
    color: "#9ca3af",
  },
  modalProfileLink: {
    marginTop: 6,
    fontSize: 12,
    color: "#2563eb",
    textDecoration: "none",
    fontWeight: 500,
  },
  modalDetailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 12,
    marginTop: 8,
  },
  modalDetailCard: {
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
    padding: 10,
  },
  modalDetailLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: "#6b7280",
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  modalDetailValue: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
  },
};

export default OrganigramaGraphs;


